import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Cell, Row} from "@khanacademy/wonder-blocks-grid";
import {Spring, Strut} from "@khanacademy/wonder-blocks-layout";
import Link from "@khanacademy/wonder-blocks-link";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    Body,
    HeadingLarge,
    HeadingSmall,
    Title,
} from "@khanacademy/wonder-blocks-typography";
import {mediaQueries} from "@repo/shared-styles";
import {staticUrl} from "@repo/urls";

import PageRoot from "#app-shell-package/page-root.tsx";
import AppStoreBadge from "#page-package/store-badges/app-store-badge.tsx";
import GooglePlayBadge from "#page-package/store-badges/google-play-badge.tsx";

import {withStrings} from "./locales/downloads-page/load-strings.tsx";

const Factoid = (props: {
    title: string;
    subtitle: string;
}): React.ReactElement => (
    <Cell largeCols={4} mediumCols={4} smallCols={4}>
        <HeadingSmall>{props.title}</HeadingSmall>
        <Strut size={8} />
        <Body>{props.subtitle}</Body>
    </Cell>
);

const SpreadsheetLink = (props: {
    title: string;
    link: string;
}): React.ReactElement => (
    <li>
        <Body>
            <a href={props.link}>{props.title}</a>
        </Body>
    </li>
);

const DownloadsPage = (): React.ReactElement => {
    return (
        <PageRoot settings={{title: t`Downloads`}}>
            <View>
                <Strut size={spacing.xxxLarge_64} />
                <Row>
                    <Cell largeCols={6} mediumCols={5} smallCols={4}>
                        <Title>{t`Downloads`}</Title>
                        <Strut size={spacing.medium_16} />
                        <Body>
                            {t`With our iOS and Android apps, you can learn anything on just-about any device! They’re all 100% free, with no in-app purchases or subscriptions.`}
                        </Body>
                    </Cell>
                </Row>

                <Strut size={spacing.xxxLarge_64} />

                <Row>
                    <Cell
                        largeCols={12}
                        mediumCols={8}
                        smallCols={4}
                        style={styles.khanAcademyContainer}
                    >
                        <View>
                            <HeadingLarge>{t`Khan Academy`}</HeadingLarge>
                            <Strut size={spacing.medium_16} />
                            <Body>
                                {t`Download videos for offline viewing, try our massive library of interactive exercises, bookmark things to study later, and complete assignments from your classes! Your progress stays in-sync with your Khan Academy account.`}
                            </Body>
                            <Strut size={spacing.medium_16} />
                            <View style={styles.appBadgesContainer}>
                                <AppStoreBadge
                                    safeAppDownloadUrl="https://apps.apple.com/app/khan-academy/id469863705"
                                    style={{height: spacing.xxLarge_48}}
                                />
                                <Strut size={spacing.medium_16} />
                                <GooglePlayBadge
                                    safeAppDownloadUrl="https://play.google.com/store/apps/details?id=org.khanacademy.android"
                                    style={{height: spacing.xxLarge_48}}
                                />
                            </View>
                        </View>
                        <View style={styles.heroImageContainer}>
                            <img
                                style={{width: "100%", height: "auto"}}
                                src={staticUrl(
                                    "/images/downloads-page/DownloadsHeroPhoto.png",
                                )}
                                alt={t`Screenshots of our iOS and Android apps`}
                            />
                        </View>
                    </Cell>
                </Row>

                <Strut size={spacing.xxxLarge_64} />

                <Row>
                    <Cell
                        largeCols={12}
                        mediumCols={8}
                        smallCols={4}
                        style={styles.factoidsContainer}
                    >
                        <Factoid
                            title={t`Free for all learners.`}
                            subtitle={t`Khan Academy is a non-profit, with a mission to provide a world-class education for any learner, anywhere.`}
                        />
                        <Factoid
                            title={t`Master thousands of skills.`}
                            subtitle={t`Our mastery system, featuring thousands of interactive exercises, is easy to use in the app.`}
                        />
                        <Factoid
                            title={t`Keep going, keep growing.`}
                            subtitle={t`Your progress in the app is kept in-sync with your progress on our website!`}
                        />
                        <Factoid
                            title={t`Keep learning, even offline.`}
                            subtitle={t`Download any video from our library, so you can watch it even when you don’t have an internet connection.`}
                        />
                        <Factoid
                            title={t`Keep up with your classes.`}
                            subtitle={t`If you’re enrolled in a class on Khan Academy, you'll see your assignments, course-mastery goals, and more.`}
                        />
                        <Factoid
                            title={t`No paper? No problem.`}
                            subtitle={t`Our interactive exercises have a lightweight scratchpad to help you doodle your way through.`}
                        />
                    </Cell>
                </Row>

                <Strut size={spacing.xxxLarge_64 + spacing.xLarge_32} />

                <Row style={styles.khanKidsBackground}>
                    <Cell largeCols={6} mediumCols={5} smallCols={4}>
                        <HeadingLarge>{t`Khan Kids`}</HeadingLarge>
                        <Strut size={spacing.medium_16} />
                        <Body>
                            {t`Inspire a lifetime of learning and discovery with our free, fun educational program for children ages two to eight.`}
                        </Body>
                        <Strut size={spacing.medium_16} />
                        <View style={{flexDirection: "row"}}>
                            <Button href="/kids">{t`Learn more`}</Button>
                            <Spring />
                        </View>
                    </Cell>
                    <Spring />
                    <Cell largeCols={4} mediumCols={3} smallCols={0}>
                        <img
                            style={{maxWidth: "100%"}}
                            src={staticUrl(
                                "/images/downloads-page/DownloadsKhanKids.png",
                            )}
                            alt={t`Characters from the Khan Kids app`}
                        />
                    </Cell>
                </Row>

                <Strut size={spacing.xxLarge_48} />

                <Row style={styles.simpleBackground}>
                    <Cell largeCols={6} mediumCols={5} smallCols={4}>
                        <HeadingLarge>{t`Third-party Apps`}</HeadingLarge>
                        <Strut size={spacing.medium_16} />
                        <Body>
                            <Trans>
                                If you need to use Khan Academy in an
                                environment without internet, consider using{" "}
                                <Link href="https://learningequality.org/kolibri">
                                    Kolibri
                                </Link>
                                , an ed-tech platform created by{" "}
                                <Link href="https://learningequality.org">
                                    Learning Equality
                                </Link>{" "}
                                that allows for Khan Academy videos and
                                exercises to be used offline.
                            </Trans>
                        </Body>
                        <Strut size={spacing.medium_16} />
                    </Cell>
                    <Spring />
                    <Cell largeCols={4} mediumCols={3} smallCols={0}>
                        <img
                            style={{maxWidth: "100%"}}
                            src={staticUrl(
                                "/images/downloads-page/Kolibri.png",
                            )}
                            alt={t`Kolibri's logo`}
                        />
                    </Cell>
                </Row>

                <Strut size={spacing.xxLarge_48} />

                <Row style={styles.simpleBackground}>
                    <Cell
                        largeCols={12}
                        mediumCols={8}
                        smallCols={4}
                        style={{
                            flexDirection: "column",
                            justifyContent: "flex-start",
                        }}
                    >
                        <HeadingLarge>{t`Spreadsheets`}</HeadingLarge>
                        <Strut size={spacing.medium_16} />
                        <Body>
                            {t`These example spreadsheets are mentioned in some Khan Academy videos. Download and work with them while watching.`}
                        </Body>
                        <Strut size={spacing.medium_16} />
                        <ul>
                            <SpreadsheetLink
                                title={t`Fixed Mortage Calculator`}
                                link={staticUrl(
                                    "/downloads/MortgageCalculator.xlsx",
                                )}
                            />
                            <SpreadsheetLink
                                title={t`Introduction to the Normal Distribution`}
                                link={staticUrl("/downloads/NormalIntro.xls")}
                            />
                            <SpreadsheetLink
                                title={t`Binomial and Normal Distribution Comparison`}
                                link={staticUrl(
                                    "/downloads/NormalDistribution.xls",
                                )}
                            />
                            <SpreadsheetLink
                                title={t`Sal's Cupcake Factory`}
                                link={staticUrl("/downloads/cupcakes.xlsx")}
                            />
                            <SpreadsheetLink
                                title={t`Home Purchase Model`}
                                link={staticUrl("/downloads/buyrent.xls")}
                            />
                        </ul>
                    </Cell>
                </Row>

                <Strut size={spacing.xxxLarge_64} />
            </View>
        </PageRoot>
    );
};

const styles = StyleSheet.create({
    khanAcademyContainer: {
        flex: 1,
        flexDirection: "row",
        justifyContent: "space-between",

        [mediaQueries.smOrSmaller]: {
            flexDirection: "column-reverse",
        },
    },
    appBadgesContainer: {
        flex: 1,
        flexDirection: "row",
        justifyContent: "flex-start",
        marginTop: spacing.medium_16,

        [mediaQueries.smOrSmaller]: {
            flexDirection: "column",
        },
    },
    heroImageContainer: {
        marginLeft: spacing.xLarge_32,

        [mediaQueries.md]: {
            marginLeft: spacing.medium_16,
        },

        [mediaQueries.smOrSmaller]: {
            marginLeft: 0,
        },
    },
    factoidsContainer: {
        display: "grid",
        gridTemplateColumns: "auto auto auto",
        gridRowGap: spacing.xxxLarge_64,
        gridColumnGap: spacing.xxxLarge_64,

        [mediaQueries.md]: {
            gridTemplateColumns: "auto auto",
            gridColumnGap: spacing.xxLarge_48,
            gridRowGap: spacing.xxLarge_48,
        },

        [mediaQueries.smOrSmaller]: {
            gridTemplateColumns: "auto",
            gridRowGap: spacing.xLarge_32,
        },
    },
    khanKidsBackground: {
        backgroundColor: "#CFE7F4",
        paddingTop: spacing.xLarge_32,
        paddingBottom: spacing.xLarge_32,
    },
    simpleBackground: {
        backgroundColor: "#F7F5F",
        borderRadius: spacing.medium_16,
        paddingTop: spacing.xLarge_32,
        paddingBottom: spacing.xLarge_32,
    },
});

export default withStrings(DownloadsPage);
